import { useNavigate } from 'react-router-dom';
import { Table } from 'components/common/Table/Table';
import * as S from './ResultPage.styles';
import { useEffect, useState } from 'react';
import { Button } from '@app/components/common/buttons/Button/Button';
import { EyeOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { notificationController } from '@app/controllers/notificationController';
import { getMockTestsResult, IResult } from '@app/api/result.api';
import { PageTitle } from '../common/PageTitle/PageTitle';
import { useTranslation } from 'react-i18next';

interface Column {
  title: string;
  dataIndex: string;
  key?: string;
  render?: (text: any, record: any, index: number) => JSX.Element;
  width?: string;
}

export const ResultPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [columns, setColumns] = useState<Column[]>([]);
  const [data, setData] = useState<IResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMockTestResult();
  }, []);

  useEffect(() => {
    initTable();
  }, [t]);

  const initTable = () => {
    setColumns([
      {
        title: '#',
        dataIndex: 'index',
        key: 'index',
        render: (_: any, __: any, index: number) => <span>{index + 1}</span>,
      },
      { title: t('page.results.mockName'), dataIndex: 'name' },
      {
        title: t('page.results.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => {
          const statusColors: Record<string, string> = {
            DONE: 'success',
            IN_PROGRESS: 'processing',
            CANCELLED: 'error',
            FINISHED: 'warning',
          };

          return (
            <Tag color={statusColors[status] || 'default'} key={status}>
              {t(`page.results.${status}`)}
            </Tag>
          );
        },
      },
      { title: t('common.speaking'), dataIndex: 'speaking' },
      { title: t('common.writing'), dataIndex: 'writing' },
      { title: t('common.reading'), dataIndex: 'reading' },
      { title: t('common.listening'), dataIndex: 'listening' },
      { title: t('page.results.score'), dataIndex: 'score' },
      { title: t('page.results.createdDate'), dataIndex: 'date' },
      {
        title: t('page.results.actions'),
        dataIndex: 'actions',
        width: '15%',
        render: (_: any, record: { name: string; UserMockTestId: string }) => {
          return (
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record?.UserMockTestId)}
            ></Button>
          );
        },
      },
    ]);
  };

  const fetchMockTestResult = async () => {
    try {
      setLoading(true);
      const res = await getMockTestsResult();
      setData(res);
    } catch (error) {
      notificationController.error({
        message: error,
      });
    }
    setLoading(false);
  };

  const handleViewDetails = (id: string) => {
    navigate(`/result/${id}`);
  };

  return (
    <>
      <PageTitle>{t('common.results')}</PageTitle>
      <div className="flex flex-col rounded-lg bg-white p-8">
        <S.Title level={3}>{t('common.results')}</S.Title>
        <div>
          <p className="mt-2 text-sm">{t('page.results.resultPageDescription')}</p>

          <Table
            columns={columns}
            dataSource={data}
            rowKey="UserMockTestId"
            loading={loading}
            scroll={{ x: 800 }}
            bordered
          />
        </div>
      </div>
    </>
  );
};
